import type { SiteViewpoint } from '../../lib/employerSiteViewpoints';

export const SITE_ROLE_COLOR = {
  BUILDING: '#1565c0',
  ENTRANCE: '#2e7d32',
  EXIT: '#b26a00',
  SIDE: '#546e7a',
} as const;

export type SiteMarkerHandle = {
  element: HTMLElement;
  setActive: (active: boolean) => void;
};

function labelPill(text: string, background: string): HTMLElement {
  const pill = document.createElement('span');
  pill.textContent = text;
  pill.style.background = background;
  pill.style.color = '#fff';
  pill.style.font = '600 11px/1.2 system-ui, -apple-system, "Segoe UI", sans-serif';
  pill.style.padding = '3px 7px';
  pill.style.borderRadius = '6px';
  pill.style.whiteSpace = 'nowrap';
  pill.style.boxShadow = '0 2px 6px rgba(0,0,0,0.35)';
  return pill;
}

export function buildingPinElement(employerName: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.display = 'grid';
  wrap.style.justifyItems = 'center';
  wrap.style.gap = '3px';
  wrap.style.transform = 'translateY(-6px)';

  const dot = document.createElement('div');
  dot.style.width = '20px';
  dot.style.height = '20px';
  dot.style.borderRadius = '50%';
  dot.style.background = SITE_ROLE_COLOR.BUILDING;
  dot.style.border = '3px solid #fff';
  dot.style.boxShadow = '0 2px 8px rgba(21,101,192,0.6)';

  wrap.append(dot, labelPill(employerName, SITE_ROLE_COLOR.BUILDING));
  return wrap;
}

/**
 * Numbered camera pin. Returns a `setActive` hook so selecting a viewpoint in the
 * rail can highlight the map pin without rebuilding every marker.
 */
export function viewpointPinElement(viewpoint: SiteViewpoint, active: boolean): SiteMarkerHandle {
  const color = SITE_ROLE_COLOR[viewpoint.role];

  const wrap = document.createElement('button');
  wrap.type = 'button';
  wrap.setAttribute('aria-label', `${viewpoint.title}. ${viewpoint.purpose}`);
  wrap.style.all = 'unset';
  wrap.style.display = 'grid';
  wrap.style.justifyItems = 'center';
  wrap.style.gap = '2px';
  wrap.style.cursor = 'pointer';

  const badge = document.createElement('div');
  badge.textContent = String(viewpoint.sequence);
  badge.style.display = 'grid';
  badge.style.placeItems = 'center';
  badge.style.width = '22px';
  badge.style.height = '22px';
  badge.style.borderRadius = '50%';
  badge.style.background = color;
  badge.style.color = '#fff';
  badge.style.font = '700 12px/1 system-ui, -apple-system, "Segoe UI", sans-serif';
  badge.style.border = '2px solid #fff';
  badge.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';

  const caption = labelPill(viewpoint.title, color);

  wrap.append(badge, caption);

  const setActive = (isActive: boolean) => {
    badge.style.transform = isActive ? 'scale(1.25)' : 'scale(1)';
    badge.style.outline = isActive ? `3px solid ${color}55` : 'none';
    caption.style.opacity = isActive ? '1' : '0.9';
    caption.style.fontWeight = isActive ? '800' : '600';
  };
  setActive(active);

  return { element: wrap, setActive };
}

export function siteRadiusRingOptions(): google.maps.CircleOptions {
  return {
    strokeColor: SITE_ROLE_COLOR.BUILDING,
    strokeOpacity: 0.75,
    strokeWeight: 1.5,
    fillColor: SITE_ROLE_COLOR.BUILDING,
    fillOpacity: 0.06,
    clickable: false,
  };
}
